import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarCheck } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { CATEGORIES } from "@/constants/site";

const TABS = [{ id: "all", label: "Tous" }, ...CATEGORIES];

const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

export const CatalogueGrid = ({ onQuote, onReserve }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/listings")
      .then((res) => setListings(res.data))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tab === "all" ? listings : listings.filter((l) => l.category === tab);

  return (
    <section id="catalogue" data-testid="catalogue-grid" className="py-24 sm:py-32 bg-[#0D1117]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E5A93C] mb-4">Catalogue</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#F0F6FC] leading-tight">
              Disponible dès maintenant
            </h2>
          </div>
          <p className="font-mono text-xs text-[#8B949E] tracking-wider" data-testid="catalogue-count">
            {filtered.length} offre{filtered.length > 1 ? "s" : ""}
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="catalogue-filters">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`filter-${t.id}-tab`}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-colors duration-300 ${
                tab === t.id
                  ? "bg-[#F2541B] border-[#F2541B] text-white"
                  : "border-[#21262D] text-[#8B949E] hover:border-[#F2541B]/60 hover:text-[#F0F6FC]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalogue-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-[#161B22] border border-[#21262D] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-[#8B949E] py-16 text-center" data-testid="catalogue-empty">
            Aucune offre dans cette catégorie pour le moment.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((l, i) => (
              <motion.article
                key={l.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                data-testid={`listing-card-${l.id}`}
                className="group bg-[#161B22] border border-[#21262D] rounded-2xl overflow-hidden transition-colors duration-300 hover:border-[#F2541B]/60"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={imgSrc(l.image_url)}
                    alt={l.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161B22] via-transparent to-transparent" />
                  <span
                    data-testid={`listing-status-${l.id}`}
                    className={`absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${
                      l.available
                        ? "bg-[#1B3B2B]/90 border-[#2D5A40] text-[#7BD8A4]"
                        : "bg-[#3B1B1B]/90 border-[#5A2D2D] text-[#E5A93C]"
                    }`}
                  >
                    {l.available ? "Disponible" : "Sur demande"}
                  </span>
                </div>

                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5A93C] mb-2">
                    {catLabel(l.category)}
                  </p>
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg text-[#F0F6FC] leading-snug">
                    {l.title}
                  </h3>
                  <p className="text-sm text-[#8B949E] mt-2 line-clamp-2">{l.description}</p>

                  {l.specs?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {l.specs.slice(0, 3).map((s) => (
                        <span key={s} className="font-mono text-[10px] text-[#8B949E] border border-[#21262D] rounded-full px-2.5 py-1">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 pt-5 border-t border-[#21262D]">
                    <span className="font-mono font-bold text-[#F2541B] text-lg block mb-4" data-testid={`listing-price-${l.id}`}>
                      {l.price > 0 ? `${l.price} ${l.price_unit}` : l.price_unit}
                    </span>
                    <div className="flex items-center gap-3">
                      {l.available && (
                        <button
                          onClick={() => onReserve(l)}
                          data-testid={`listing-reserve-${l.id}`}
                          className="flex-1 flex items-center justify-center gap-2 bg-[#F2541B] hover:bg-[#D9420B] text-white font-display font-bold uppercase text-sm tracking-wide px-4 py-2.5 rounded-full transition-colors duration-300"
                        >
                          <CalendarCheck size={15} /> Réserver
                        </button>
                      )}
                      <button
                        onClick={() => onQuote(l.category, l.title)}
                        data-testid={`listing-devis-${l.id}`}
                        className={`flex items-center justify-center gap-1.5 font-display font-bold uppercase text-sm tracking-wide px-4 py-2.5 rounded-full border transition-colors duration-300 ${
                          l.available
                            ? "border-[#21262D] text-[#F0F6FC] hover:border-[#F2541B] hover:text-[#F2541B]"
                            : "flex-1 border-[#F2541B] text-[#F2541B] hover:bg-[#F2541B] hover:text-white"
                        }`}
                      >
                        Devis <ArrowUpRight size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
